/* eslint-disable consistent-return */
/* eslint-disable no-case-declarations */
/* eslint-disable default-case */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as dcmjs from 'dcmjs';
import * as dicomParser from 'dicom-parser';
import { HtmlStyled } from './styled/HtmlStyled';
import * as ArrayUtils from '../../../../model/ArrayUtils';
import { Loading } from '../../Common/Loading';
import { useSelector } from 'react-redux';
import { WADO_URI_WEB_SERVICE_TYPE, WADO_RS_WEB_SERVICE_TYPE } from '../../../../util/useServerConfig';
import { getHeadersConfig } from '../../../../util/getHeadersConfig';
import { getPacsEndpoints } from '../../../../util/getConfig';

const extractPDF = (byteArray) => {
  const dataset = dicomParser.parseDicom(byteArray);
  const encapsulatedDocumentMimeType = dataset.string('x00420012');
  const encapsulatedDocumentElement = dataset.elements.x00420011;
  if (!encapsulatedDocumentMimeType || encapsulatedDocumentMimeType !== 'application/pdf' || !encapsulatedDocumentElement) {
    throw new Error('Not a valid DICOM encapsulated PDF file.');
  }

  const pdfBuffer = new Uint8Array(dataset.byteArray.buffer, encapsulatedDocumentElement.dataOffset, encapsulatedDocumentElement.length);

  const pdfBlob = new Blob([pdfBuffer], {
    type: 'application/pdf',
  });

  const objectURL = URL.createObjectURL(pdfBlob);

  return objectURL;
};

const getRelationshipString = (data) => {
  switch (data.RelationshipType) {
    case 'HAS CONCEPT MOD':
      return 'Concept modifier: ';
    case 'HAS OBS CONTEXT':
      return 'Observation context: ';
    default:
      return '';
  }
};

const getMeaningString = (data) => {
  if (data.ConceptNameCodeSequence) {
    const { CodeMeaning } = data.ConceptNameCodeSequence;

    return `${CodeMeaning} = `;
  }

  return '';
};

const getValueString = (data) => {
  switch (data.ValueType) {
    case 'CODE':
      const { CodeMeaning, CodeValue, CodingSchemeDesignator } = data.ConceptNameCodeSequence;

      return `${CodeMeaning} (${CodeValue}, ${CodingSchemeDesignator})`;

    case 'PNAME':
      return data.PersonName;

    case 'TEXT':
      return data.TextValue;

    case 'UIDREF':
      return data.UID;

    case 'NUM':
      const { MeasuredValueSequence } = data;
      const numValue = MeasuredValueSequence.NumericValue;
      const codeValue = MeasuredValueSequence.MeasurementUnitsCodeSequence.CodeValue;
      return `${numValue} ${codeValue}`;
  }
};

const constructPlainValue = (data) => {
  const value = getValueString(data);

  if (value) {
    return getRelationshipString(data) + getMeaningString(data) + value;
  }
};

const constructContentSequence = (data, header) => {
  if (!data.ContentSequence) {
    return;
  }

  const items = data.ContentSequence.map((item) => parseContent(item)).filter((item) => item);

  if (!items.length) {
    return;
  }

  const result = {
    items,
  };

  if (header) {
    result.header = header;
  }

  return result;
};

const parseContent = (data) => {
  if (data.ValueType) {
    if (data.ValueType === 'CONTAINER') {
      const Header = data.ConceptNameCodeSequence.CodeMeaning;

      return constructContentSequence(data, Header);
    }

    return constructPlainValue(data);
  }

  if (data.ContentSequence) {
    return constructContentSequence(data);
  }
};

const { DicomMetaDictionary, DicomMessage } = dcmjs.data;

const getMainData = (data) => {
  const root = [];

  const patientValue = `${data.PatientName} (${data.PatientSex}, #${data.PatientID})`;
  root.push(getMainDataItem('Patient', patientValue));

  const studyValue = data.StudyDescription;
  root.push(getMainDataItem('Study', studyValue));

  const seriesValue = `${data.SeriesDescription} (#${data.SeriesNumber})`;
  root.push(getMainDataItem('Series', seriesValue));

  const manufacturerValue = `${data.Manufacturer} (${data.ManufacturerModelName}, #${data.DeviceSerialNumber})`;

  root.push(getMainDataItem('Manufacturer', manufacturerValue));

  const mainDataObjects = {
    CompletionFlag: 'Completion flag',
    VerificationFlag: 'Verification flag',
  };

  Object.keys(mainDataObjects).forEach((key) => {
    if (!data[key]) {
      return;
    }

    const item = getMainDataItem(mainDataObjects[key], data[key]);

    root.push(item);
  });

  // TODO: Format these dates
  const contentDateTimeValue = `${data.ContentDate} ${data.ContentTime}`;
  root.push(getMainDataItem('Content Date/Time', contentDateTimeValue));

  root.push();

  return <div>{root}</div>;
};

const getContentSequence = (data, index, level = 1) => {
  let Header;

  if (data.ConceptNameCodeSequence) {
    const { CodeMeaning, CodeValue, CodingSchemeDesignator } = data.ConceptNameCodeSequence;

    Header = `${CodeMeaning} (${CodeValue} - ${CodingSchemeDesignator})`;
  }

  const root = [];
  if (Header) {
    const HeaderDynamicLevel = `h${level}`;
    const key = Header;
    root.push(<HeaderDynamicLevel key={key}>{Header}</HeaderDynamicLevel>);
  }

  Object.keys(data).forEach((key) => {
    const value = data[key];

    let content;
    if (value instanceof Object) {
      content = getContentSequence(value, index, level + 1);
      index++;
    } else {
      content = (
        <div key={key}>
          <b>{key}</b> - {data[key]}
        </div>
      );
    }

    root.push(content);
  });

  const divKey = level + '-' + index + '-' + root.length;
  return <div key={divKey}>{root}</div>;
};

const getMainDataItem = (key, value) => (
  <div key={`${key} + ${Math.random() * 1010}`}>
    <b>{key}</b>: {value}
  </div>
);

export const Html = ({ instanceIds, index, layoutElement }) => {
  const [content, setContent] = useState(<Loading />);
  const [stateInstancesIds, setStateInstanceIds] = useState([]);
  const config = useSelector((state) => state.config);

  useEffect(() => {
    if (instanceIds.length !== 1) {
      setContent(
        <>
          Invalid instances length. Expected 1, but
          {instanceIds.length} given
        </>
      );
    }
  }, []);

  const getDicom = (response) => {
    const StartArray = new Uint8Array([13, 10, 13, 10]);
    const EndArray = new Uint8Array([10, 13]);

    const arrayBuffer = response.data;
    const byteArray = new Uint8Array(arrayBuffer);
    const length = byteArray.length;

    let StartExtraction = 0;
    let EndExtraction = 0;
    let PostionArrayFind = 0;

    // Find StartArray in byteArray
    for (let k = 0; k < 800; k++) {
      if (byteArray[k] === StartArray[PostionArrayFind]) {
        if (StartArray.length - 1 === PostionArrayFind) {
          StartExtraction = k + 1;
          break;
        } else {
          PostionArrayFind += 1;
        }
      } else {
        PostionArrayFind = 0;
      }
    }

    // Find EndArray in byteArray, inverse
    PostionArrayFind = 0;
    for (let k = 2; k < 100; k++) {
      let j = length - k;
      if (byteArray[j] === EndArray[PostionArrayFind]) {
        if (EndArray.length - 1 === PostionArrayFind) {
          EndExtraction = -k;
          break;
        } else {
          PostionArrayFind += 1;
        }
      } else {
        PostionArrayFind = 0;
      }
    }

    return byteArray.slice(StartExtraction, EndExtraction);
  };

  useEffect(() => {
    const fetchImage = async () => {
      const instanceId = instanceIds[0];

      const cfg = getHeadersConfig(config.authorizationHeader);

      let response;
      let dcm;
      const { wadoUriEnabled } = getPacsEndpoints(config);
      const webServiceType = wadoUriEnabled ? WADO_URI_WEB_SERVICE_TYPE : config.WEB_SERVICE_TYPE || WADO_RS_WEB_SERVICE_TYPE;
      if (webServiceType === WADO_RS_WEB_SERVICE_TYPE) {
        response = await axios(instanceId, cfg);
        dcm = getDicom(response);
      } else {
        response = await axios(instanceId, cfg);
        dcm = response.data;
      }
      const byteArray = new Uint8Array(dcm);
      const arrayBuffer = byteArray.buffer;
      const dicomData = DicomMessage.readFile(arrayBuffer);

      const dataset = DicomMetaDictionary.naturalizeDataset(dicomData.dict);
      dataset._meta = DicomMetaDictionary.namifyDataset(dicomData.meta);

      if (dataset.Modality === 'PR') {
        const mainData = getMainData(dataset);
        const contentSequence = getContentSequence(dataset, 0);
      }

      if (dataset.Modality === 'DOC') {
        const objectURL = extractPDF(byteArray);
        setContent(
          <HtmlStyled>
            <object data={objectURL} type="application/pdf" width="100%" height="100%" />
          </HtmlStyled>
        );
      } else {
        const mainData = getMainData(dataset);
        const contentSequence = getContentSequence(dataset, 0);
        setContent(
          <HtmlStyled>
            {mainData}
            {contentSequence}
          </HtmlStyled>
        );
      }
    };

    if (!ArrayUtils.areEqual(stateInstancesIds, instanceIds)) {
      setStateInstanceIds(instanceIds);
      fetchImage();
    }
  }, [instanceIds]);

  return content;
};
